import { type ConfirmedQuantity, type OrderConfirmationRow } from '@/types/orderConfirmation'
import type { PurchaseOrderRow } from '@/types/purchaseOrder'
import type { OrderConfirmationSettings } from '@/types/orderConfirmationSettings'

export type DeviationLevel = 'none' | 'warn' | 'error'

export function deviationClass(level: DeviationLevel): string {
  switch (level) {
    case 'warn':  return 'font-semibold text-amber-700'
    case 'error': return 'font-semibold text-red-600'
    default:      return ''
  }
}

function dateOnly(value: string | undefined): string {
  if (!value?.trim()) return ''
  const t = value.trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t
  const d = new Date(t)
  return Number.isNaN(d.getTime()) ? t : d.toISOString().slice(0, 10)
}

function daysBetween(earlier: string, later: string): number {
  const a = dateOnly(earlier)
  const b = dateOnly(later)
  if (!a || !b) return 0
  const ms = new Date(b).getTime() - new Date(a).getTime()
  if (!Number.isFinite(ms)) return 0
  return Math.max(0, Math.floor(ms / 86_400_000))
}

/** Calendar days the arrival date is after the requested delivery date (0 if on time or early). */
export function daysLate(arrivalDate: string, row: PurchaseOrderRow): number {
  const baselineDay = dateOnly(row.requested_delivery_date)
  const lineDay = dateOnly(arrivalDate)
  if (!baselineDay || !lineDay || lineDay <= baselineDay) return 0
  return daysBetween(baselineDay, lineDay)
}

/** Calendar days the arrival date is before the requested delivery date (0 if on time or late). */
export function daysEarly(arrivalDate: string, row: PurchaseOrderRow): number {
  const baselineDay = dateOnly(row.requested_delivery_date)
  const lineDay = dateOnly(arrivalDate)
  if (!baselineDay || !lineDay || lineDay >= baselineDay) return 0
  return daysBetween(lineDay, baselineDay)
}

/** warn when arrival deviates >= threshold days from requested (late or early). */
export function dateDeviation(
  arrivalDate: string,
  row: PurchaseOrderRow,
  settings: OrderConfirmationSettings | undefined,
  highlight: boolean,
): DeviationLevel {
  if (!highlight || !settings) return 'none'
  if (daysLate(arrivalDate, row) >= settings.days_of_delay_deviation_threshold) return 'warn'
  if (daysEarly(arrivalDate, row) >= settings.days_of_early_deviation_threshold) return 'warn'
  return 'none'
}

/** warn when relative price deviation >= threshold; no highlight within threshold. */
export function priceDeviation(
  lineUnitPrice: number | null,
  row: PurchaseOrderRow,
  settings: OrderConfirmationSettings | undefined,
  highlight: boolean,
): DeviationLevel {
  if (!highlight || !settings) return 'none'
  const baseline = row.unit_price
  if (lineUnitPrice == null || !Number.isFinite(baseline) || !Number.isFinite(lineUnitPrice) || baseline <= 0) return 'none'
  const relPct = (Math.abs(lineUnitPrice - baseline) / baseline) * 100
  if (relPct < settings.relative_price_difference_deviation_threshold) return 'none'
  return 'warn'
}

/** Sum of all confirmed quantities on matched OC rows for this PO line. */
export function totalConfirmedQuantityForPoRow(row: PurchaseOrderRow): number {
  let sum = 0
  for (const ocRow of row.matched_order_confirmation_rows ?? []) {
    sum += totalConfirmedQuantityForOcRow(ocRow)
  }
  return sum
}

export function totalConfirmedQuantityForOcRow(ocRow: OrderConfirmationRow): number {
  let sum = 0
  for (const cq of ocRow.confirmed_quantities ?? []) {
    const q = cq.quantity
    if (Number.isFinite(q) && q > 0) sum += q
  }
  return sum
}

/** error = confirmed > requested; warn = confirmed < requested (partial); none = exact match. */
export function poRowQuantityDeviation(row: PurchaseOrderRow): DeviationLevel {
  const expected = row.requested_quantity
  if (!Number.isFinite(expected)) return 'none'
  const confirmed = totalConfirmedQuantityForPoRow(row)
  if (confirmed > expected) return 'error'
  if (confirmed < expected) return 'warn'
  return 'none'
}

export function rowDeviationAnchorId(position: string): string {
  return `oc-row-pos-${position}`
}

export function priceDeviationPercent(lineUnitPrice: number | null, row: PurchaseOrderRow): number {
  const baseline = row.unit_price
  if (lineUnitPrice == null || !Number.isFinite(baseline) || baseline <= 0 || !Number.isFinite(lineUnitPrice)) return 0
  return (Math.abs(lineUnitPrice - baseline) / baseline) * 100
}

export type LineDeviationDetail =
  | { kind: 'delay'; rowPosition: string; rowAnchorId: string; daysLate: number }
  | { kind: 'early'; rowPosition: string; rowAnchorId: string; daysEarly: number }
  | { kind: 'price'; rowPosition: string; rowAnchorId: string; percent: number }

export function collectThresholdExceededLineDeviations(
  poRows: PurchaseOrderRow[],
  settings: OrderConfirmationSettings | undefined,
  highlight: boolean,
): LineDeviationDetail[] {
  const out: LineDeviationDetail[] = []

  for (const row of poRows) {
    const anchorId = rowDeviationAnchorId(row.position)
    const matched = row.matched_order_confirmation_rows ?? []

    for (const ocRow of matched) {
      for (const cq of ocRow.confirmed_quantities ?? []) {
        const arrivalDate = (cq as ConfirmedQuantity).arrival_date?.trim() ?? ''
        if (dateDeviation(arrivalDate, row, settings, highlight) === 'none') continue
        const late = daysLate(arrivalDate, row)
        const early = daysEarly(arrivalDate, row)
        if (late > 0) {
          out.push({ kind: 'delay', rowPosition: row.position, rowAnchorId: anchorId, daysLate: late })
        } else if (early > 0) {
          out.push({ kind: 'early', rowPosition: row.position, rowAnchorId: anchorId, daysEarly: early })
        }
      }

      if (priceDeviation(ocRow.unit_price, row, settings, highlight) !== 'none') {
        out.push({
          kind: 'price',
          rowPosition: row.position,
          rowAnchorId: anchorId,
          percent: priceDeviationPercent(ocRow.unit_price, row),
        })
      }
    }
  }

  return out
}
